import AppLayout from "@/layouts/AppLayout";
import EmptyState from "@/components/EmptyState";
import Tabs from "@/components/Tabs";
import { TableHeading } from "@/components/Table";
import useCapabilities from "@/hooks/useCapabilities";
import {
  CAREER_SERVICES_TAB,
  MEMBERSHIP_SERVICES_TAB,
  useFirstUsableAnalyticsTab,
  useInteractionAnalyticsTabs
} from "@/hooks/useInteractionAnalyticsTabs";
import { useCanAccessInteractionAnalytics } from "@/hooks/useIsHqStaff";
import usePageTitle from "@/hooks/usePageTitle";
import { Path, useNavigate } from "@/router";
import { Navigate, Outlet, useLocation } from "react-router-dom";

export default function InteractionAnalyticsLayout() {
  usePageTitle("/analytics/interactions", "Interaction Analytics");
  const { loaded } = useCapabilities();
  const canViewAnalytics = useCanAccessInteractionAnalytics();
  const { canUseMembershipTab, canUseCareerTab } = useInteractionAnalyticsTabs();
  const firstUsableTab = useFirstUsableAnalyticsTab();
  const location = useLocation();
  const navigate = useNavigate();
  const normalizedPath = location.pathname.endsWith("/") ? location.pathname.slice(0, -1) : location.pathname;
  const tabItems = [
    ...(canUseMembershipTab ? [MEMBERSHIP_SERVICES_TAB] : []),
    ...(canUseCareerTab ? [CAREER_SERVICES_TAB] : [])
  ];

  if (!loaded) {
    return null;
  }

  if (!canViewAnalytics) {
    return (
      <AppLayout>
        <EmptyState
          title="Interaction Analytics is an HQ reporting view."
          caption="This dashboard is currently scoped to HQ staff and global admins."
          iconName="bar_chart"
        />
      </AppLayout>
    );
  }

  // Index, unknown, and gated-tab URLs land on the first tab the user can use.
  if (!tabItems.some(({ to }) => to === normalizedPath)) {
    if (!firstUsableTab) {
      return (
        <AppLayout>
          <EmptyState title="No Interaction Analytics views are available for your account." iconName="bar_chart" />
        </AppLayout>
      );
    }
    return <Navigate to={`${firstUsableTab}${location.search}`} replace />;
  }

  return (
    <AppLayout>
      <TableHeading heading="Interaction Analytics" isPageHeading />
      <Tabs
        tabItems={tabItems.map(({ label, to }) => ({
          label,
          onClick: () => navigate(`${to}${location.search}` as Path),
          active: normalizedPath === to
        }))}
      />
      <Outlet />
    </AppLayout>
  );
}
